import { useEffect, useState } from 'react';
import { Button, SxProps, Theme } from '@mui/material';

const DEFAULT_COOLDOWN_MS = 30_000;

export interface ResendCodeButtonProps {
    onResend: () => Promise<void> | void;
    cooldownMs?: number;
    sx?: SxProps<Theme>;
}

/** Text button that enforces a cooldown after each send (and on mount, since a code was already sent). */
const ResendCodeButton = ({
    onResend,
    cooldownMs = DEFAULT_COOLDOWN_MS,
    sx,
}: ResendCodeButtonProps) => {
    const [isSending, setIsSending] = useState(false);
    const [sendAt, setSendAt] = useState(Date.now());
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const timeLeft = Math.floor((cooldownMs - (now - sendAt)) / 1000);
    const isCoolingDown = timeLeft > 0;

    const handleClick = async () => {
        setIsSending(true);
        try {
            await onResend();
        } finally {
            setIsSending(false);
            setSendAt(Date.now());
        }
    };

    const label = isSending
        ? 'Sending...'
        : isCoolingDown
            ? `Send code again in (${timeLeft}s)`
            : 'Send code again';

    return (
        <Button
            variant="text"
            size="medium"
            disabled={isSending || isCoolingDown}
            onClick={handleClick}
            sx={[{ width: 'fit-content' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        >
            {label}
        </Button>
    );
};

export default ResendCodeButton;
